import {
  DashboardState,
  IApiManagement,
  IDatasourceDto,
  IScheduledJob,
  IWorkFlowBaseInfo,
  LeftPanelType,
  RightPanelType,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

function clearSharedEditorState(sd: IScreenDesignContextState, options?: { clearWorkflowSelection?: boolean }): void {
  const clearWorkflowSelection = options?.clearWorkflowSelection !== false;

  sd.setCreateNotificationTemplateActive?.();
  sd.clearNotificationTemplatePanel?.();
  sd.cancelEditingScheduledJob?.();
  sd.cancelEditingApi?.();
  sd.cancelActiveMethod?.();
  sd.cancelCreatingOrEditingDatasource?.();
  sd.cancelCreatingOrEditingConfiguration?.();

  if (clearWorkflowSelection) {
    sd.clearSelectedWf?.();
  }
}

export async function navigateToDesignView(sdInstance: ScreenDesignInstance): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  sd.setDashboardState?.(DashboardState.design);
  sd.setActiveRightPanelByClick?.(RightPanelType.Properties);
  clearSharedEditorState(sd);
}

export async function navigateToDatasourceView(sdInstance: ScreenDesignInstance): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  sd.setDashboardState?.(DashboardState.dataDiagramScheme);
  clearSharedEditorState(sd);
}

export async function navigateToApiManagementView(sdInstance: ScreenDesignInstance): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  sd.setDashboardState?.(DashboardState.apiManagement);
  clearSharedEditorState(sd);
  sd.setActiveApiManagementPanel?.(LeftPanelType.ApiManagement);
}

/**
 * Opens the Report Designer on its landing. It goes through `enterReportDesigner` rather than a bare
 * `setDashboardState`: the tool, its Reports panel and its landing have to land together, or the
 * UI screen that is loaded shows on the report canvas with no report selected.
 */
export async function navigateToReportDesignerView(sdInstance: ScreenDesignInstance): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  sd.enterReportDesigner?.({ showLanding: true });
  clearSharedEditorState(sd);
}

export async function navigateToScheduledJobsView(
  sdInstance: ScreenDesignInstance,
  createMode: boolean = true
): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  sd.setDashboardState?.(DashboardState.scheduledJobs);
  clearSharedEditorState(sd);
  sd.setCreateSJobActive?.(createMode);
  sd.setActiveScheduledJobsPanel?.(LeftPanelType.None);
}

export async function navigateToWorkflowView(
  sdInstance: ScreenDesignInstance,
  options?: {
    selectLastOpenWorkflow?: boolean;
    createWhenEmpty?: boolean;
    setRootSelection?: boolean;
  }
): Promise<void> {
  const sd = getValues(sdInstance);
  if (!sd) return;

  // `setDashboardState` is a TOGGLE (design-situation-provider): invoking it
  // with `workFlow` while the app is ALREADY in `workFlow` flips BACK to
  // `design` and UNMOUNTS WorkFlowWindow — which strands the BPMN modeler
  // (`window.bpmnModeler` never gets built). This helper is a PROGRAMMATIC
  // "ensure the workflow view is open" navigation, so it must be IDEMPOTENT:
  // only transition when not already there. The footer workflow-tab button
  // keeps its toggle behavior (it calls `setDashboardState` directly, not this
  // helper). (KOA-17362)
  if (sd.dashboardState !== DashboardState.workFlow) {
    sd.setDashboardState?.(DashboardState.workFlow);
  }
  if (options?.setRootSelection) {
    sd.setSelectedDisplayItem?.(SpecialDIIds.rootDI.toString());
  }

  sd.setActiveWorkFlowLeftPanel?.(LeftPanelType.WorkFlowList);
  clearSharedEditorState(sd, { clearWorkflowSelection: false });

  if (options?.selectLastOpenWorkflow !== false && sd.lastOpenWfId) {
    await sd.setSelectedWf?.(sd.lastOpenWfId);
    sd.setCreatingWfActive?.(false);
  } else if (options?.createWhenEmpty !== false && !sd.lastOpenWfId) {
    sd.setCreatingWfActive?.(true);
  }

  sd.setActiveWorkFlowRightPanel?.(RightPanelType.Properties);
  sd.setActiveRightPanelByClick?.(RightPanelType.Properties);
}

export async function openApiForEditing(
  sdInstance: ScreenDesignInstance,
  apiId: string,
  options?: { ensureView?: boolean; methodId?: string }
): Promise<IApiManagement | undefined> {
  const sd = getValues(sdInstance);
  if (!sd) return undefined;

  const api = (sd.project?.apiManagements || sd.apiManagements || []).find((item: IApiManagement) => item.id === apiId);
  if (!api) return undefined;

  if (options?.ensureView !== false) {
    await navigateToApiManagementView(sdInstance);
  }

  sd.setActiveApi?.(api.id);
  sd.setActiveMethod?.(options?.methodId || api.json?.methods?.[0]?.id);
  return api;
}

export async function openDatasourceForEditing(
  sdInstance: ScreenDesignInstance,
  datasourceId: string,
  options?: { ensureView?: boolean }
): Promise<IDatasourceDto | undefined> {
  const sd = getValues(sdInstance);
  if (!sd) return undefined;

  const datasource = (sd.datasources || []).find((item: IDatasourceDto) => String(item.id) === String(datasourceId));
  if (!datasource) return undefined;

  if (options?.ensureView !== false) {
    await navigateToDatasourceView(sdInstance);
  }

  sd.setActiveEditingDatasource?.(datasource);
  return datasource;
}

export async function openScheduledJobForEditing(
  sdInstance: ScreenDesignInstance,
  jobId: string,
  options?: { ensureView?: boolean }
): Promise<IScheduledJob | undefined> {
  const sd = getValues(sdInstance);
  if (!sd) return undefined;

  const scheduledJob = (sd.project?.scheduledJobs || sd.scheduledJobs || []).find(
    (item: IScheduledJob) => String(item.id) === String(jobId)
  );
  if (!scheduledJob) return undefined;

  if (options?.ensureView !== false) {
    await navigateToScheduledJobsView(sdInstance, false);
  }

  sd.setCreateSJobActive?.(false);
  sd.setActiveScheduledJob?.(scheduledJob.id);
  return scheduledJob;
}

export async function openWorkflowForEditing(
  sdInstance: ScreenDesignInstance,
  workflowId: string,
  options?: { ensureView?: boolean; setRootSelection?: boolean }
): Promise<IWorkFlowBaseInfo | undefined> {
  const sd = getValues(sdInstance);
  if (!sd) return undefined;

  const workflow = (sd.workFlowList || []).find((item: IWorkFlowBaseInfo) => String(item.id) === String(workflowId));
  if (!workflow) return undefined;

  if (options?.ensureView !== false) {
    await navigateToWorkflowView(sdInstance, {
      selectLastOpenWorkflow: false,
      createWhenEmpty: false,
      setRootSelection: options?.setRootSelection
    });
  }

  await sd.setSelectedWf?.(workflow.id);
  sd.setCreatingWfActive?.(false);
  sd.setActiveWorkFlowRightPanel?.(RightPanelType.Properties);
  sd.setActiveRightPanelByClick?.(RightPanelType.Properties);
  return workflow;
}
